/**
 * Shared by the feature-matrix form and the
 * `updateDecisionEngineFeatureMatrixConsentPurge` resolver so both reject the
 * same configs: the resolver enforces, the form only pre-surfaces field errors.
 * Keep this module free of backend-only dependencies (zapatos, Source, etc.) —
 * frontend code imports it via @hightouch/lib.
 */

import { USER_FEATURE_PREFIX } from "./feature-naming";
import type { ConsentPurgeConfig } from "./types";

export const CONSENT_PURGE_MODES = ["plan", "execute"] as const;
export type ConsentPurgeMode = (typeof CONSENT_PURGE_MODES)[number];
export const DEFAULT_CONSENT_PURGE_MODE: ConsentPurgeMode = "plan";

export function isConsentPurgeSupportedSource(
  sourceType: string | null | undefined,
): boolean {
  // Redshift's lossless rewrite needs customer-owned Lambda, IAM and network infrastructure; operating that runtime is out of scope for now.
  // Databricks needs a managed Unity Catalog Python function and compatible compute, which we do not provision.
  return (
    sourceType === "bigquery" ||
    sourceType === "snowflake" ||
    sourceType === "postgres"
  );
}

const TIMESTAMP_COLUMN_TYPES = new Set([
  "timestamp",
  "timestamp_with_timezone",
  "date",
]);

interface RevocationsModel {
  name: string;
  /** `segments.connection_id` of the model, as a string. */
  sourceId: string;
  columns: { name: string; type: string }[];
}

type ConsentPurgeValidationField =
  | "revocationsModel"
  | "primaryKey"
  | "revokedAt"
  | "sanitizedColumns";

export interface ConsentPurgeValidationError {
  field: ConsentPurgeValidationField;
  message: string;
}

export interface FlaggedColumn {
  modelName: string;
  sourceColumn: string;
  variantKeys: string[];
}

export interface ValidateConsentPurgeInput {
  consentPurge: ConsentPurgeConfig | null;
  /** Null means the configured model was deleted, not that none is configured. */
  revocationsModel: RevocationsModel | null;
  /** `connection_id` of the decision engine's source, as a string. */
  engineSourceId: string | null;
}

/** Returns every problem found, so the form can surface them together. */
export function validateConsentPurge({
  consentPurge,
  revocationsModel,
  engineSourceId,
}: ValidateConsentPurgeInput): ConsentPurgeValidationError[] {
  const errors: ConsentPurgeValidationError[] = [];

  if (consentPurge) {
    if (!revocationsModel) {
      errors.push({
        field: "revocationsModel",
        message: "The selected consent revocations model does not exist.",
      });
    } else {
      if (engineSourceId && revocationsModel.sourceId !== engineSourceId) {
        errors.push({
          field: "revocationsModel",
          message: `"${revocationsModel.name}" is on a different source than this agent. Select a model on the agent's source.`,
        });
      }

      const columnsByName = new Map(
        revocationsModel.columns.map((c) => [c.name, c]),
      );

      const primaryKey = consentPurge.columns.primaryKey;
      if (!primaryKey) {
        errors.push({
          field: "primaryKey",
          message: "Select the column holding the user primary key.",
        });
      } else if (!columnsByName.has(primaryKey)) {
        errors.push({
          field: "primaryKey",
          message: `"${primaryKey}" no longer exists in "${revocationsModel.name}".`,
        });
      }

      const revokedAt = consentPurge.columns.revokedAt;
      const revokedAtColumn = revokedAt
        ? columnsByName.get(revokedAt)
        : undefined;
      if (!revokedAt) {
        errors.push({
          field: "revokedAt",
          message: "Select the column holding the revocation timestamp.",
        });
      } else if (!revokedAtColumn) {
        errors.push({
          field: "revokedAt",
          message: `"${revokedAt}" no longer exists in "${revocationsModel.name}".`,
        });
      } else if (
        !TIMESTAMP_COLUMN_TYPES.has(revokedAtColumn.type.toLowerCase())
      ) {
        errors.push({
          field: "revokedAt",
          message: `"${revokedAt}" is not a timestamp or date column. Select a column recording when consent was revoked.`,
        });
      }
    }
  }

  return errors;
}

/**
 * Separate from `validateConsentPurge`, because a revocations model deleted after
 * the fact must not block every later save of the matrix.
 *
 * Pass `strataFeatures` as configured, raw or exploded, prefixed or not.
 */
export function validateSanitizeFlags({
  consentPurge,
  flaggedColumns,
  strataFeatures,
}: {
  consentPurge: ConsentPurgeConfig | null;
  flaggedColumns: FlaggedColumn[];
  strataFeatures: string[];
}): ConsentPurgeValidationError[] {
  const errors: ConsentPurgeValidationError[] = [];
  if (flaggedColumns.length > 0 && !consentPurge) {
    errors.push({
      field: "sanitizedColumns",
      message: `${describeColumns(flaggedColumns)} marked for sanitization, which needs a consent revocations model on this feature matrix. Configure one, or clear the flags.`,
    });
  }

  const strata = new Set(
    strataFeatures.map((feature) => feature.toLowerCase()),
  );
  const strataColumns = flaggedColumns.filter(
    (column) =>
      strata.has(column.sourceColumn.toLowerCase()) ||
      column.variantKeys.some(
        (key) =>
          strata.has(key) || strata.has(key.slice(USER_FEATURE_PREFIX.length)),
      ),
  );
  if (strataColumns.length > 0) {
    errors.push({
      field: "sanitizedColumns",
      message: `${describeColumns(strataColumns)} used to stratify intelligent targeting, so ${strataColumns.length === 1 ? "it" : "they"} cannot be sanitized. Drop the stratification, or clear the flag.`,
    });
  }

  return errors;
}

/**
 * Helper to ensure the correct verb when describing a column.
 *  i.e. "orders.amount is" or "3 features are"
 */
function describeColumns(columns: FlaggedColumn[]): string {
  const [only] = columns;
  if (columns.length === 1 && only) {
    return `"${only.modelName}.${only.sourceColumn}" is`;
  }
  return `${columns.length} features are`;
}
